import { clamp } from '../../model/utility';
import { DashboardGridOptions } from '../layout/dashboard-layout';

/** The tallest row height, between the grid's minimum and preferred heights, that fits `rows` rows into `availablePx` */
export function fittedRowHeightPx(
  rows: number,
  availablePx: number | null,
  { rowHeightPx, minRowHeightPx, gapPx }: DashboardGridOptions,
): number {
  if (rows === 0 || availablePx === null) {
    return rowHeightPx;
  }

  const fit = Math.floor((availablePx + gapPx) / rows - gapPx);
  return clamp(fit, Math.min(minRowHeightPx, rowHeightPx), rowHeightPx);
}

/** Height from the top of `element` to the bottom of its scroll container's viewport, less what sits below it */
export function heightBelowPx(element: HTMLElement): number {
  const scroller = scrollContainer(element);
  const trailing = spaceBelow(element, scroller);

  if (!scroller) {
    const top = element.getBoundingClientRect().top + window.scrollY;
    return window.innerHeight - top - trailing;
  }

  const top = element.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  return scroller.clientHeight - top - trailing;
}

/** The nearest ancestor that scrolls vertically; null when the window scrolls */
export function scrollContainer(element: HTMLElement): HTMLElement | null {
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }
  }

  return null;
}

/** Padding, borders and horizontal scrollbars of the ancestors below `element`, up to its scroll container */
function spaceBelow(element: HTMLElement, scroller: HTMLElement | null): number {
  let total = 0;
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    total += Number.parseFloat(style.paddingBottom);
    if (parent === scroller) {
      break;
    }

    const borders = Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth);
    total += Number.parseFloat(style.borderBottomWidth) + (parent.offsetHeight - parent.clientHeight - borders);
  }

  return total;
}
